import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { TreeView } from './TreeView';
import { ControlBar } from '../chrome/ControlBar';
import { TreeInteractorProvider, useTreeState } from '../tree-domain/TreeInteractorContext';
import { AppProviders } from '../providers/AppProviders';
import { AuthGate } from '../auth/AuthGate';
import { PrivateTreeAccessDenied } from './PrivateTreeAccessDenied';
import { buildTreeViewUrl, parseLegacyTreeViewHashNodeSegment } from '../routing/routing';
import { useIsNarrowViewport } from '../ui/useIsNarrowViewport';

const TreeViewPageContent: React.FC<{ treeId: string; urlNodeSegment: string | null }> = ({
  treeId,
  urlNodeSegment,
}) => {
  const treeState = useTreeState();
  const navigate = useNavigate();
  const isNarrowViewport = useIsNarrowViewport();
  const [showWhatIsThis, setShowWhatIsThis] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWhatIsThis(true);
    }, 4000); // 4 seconds delay
    
    return () => clearTimeout(timer);
  }, []);
  
  const handleWhatIsThisClick = () => {
    navigate('/about');
  };
  
  return (
    <div className="view-container">
      <ControlBar 
        isDraft={treeState.isDraft}
        showSaveButton={false} // Read-only mode, no save button
        isEditMode={false}
        treeId={treeId}
        isLoading={treeState.isLoading ?? false}
        canEdit={treeState.canEdit ?? false}
        treeName={treeState.tree.name}
      />
      <TreeView
        treeId={treeId}
        skillTreeData={treeState.tree}
        showDevControls={false}
        syncSelectionFromPath={true}
        urlNodeSegment={urlNodeSegment}
        hideTitleBar={isNarrowViewport}
        privateAccessDeniedView={<PrivateTreeAccessDenied />}
      />
      
      {/* Floating "What is this?" button (hidden when tree did not load, e.g. private access) */}
      {!treeState.privateAccessDenied && (
        <div 
          className={`what-is-this-button ${showWhatIsThis ? 'show' : ''}`}
          onClick={handleWhatIsThisClick}
        >
          <h3>What is this?</h3>
        </div>
      )}
    </div>
  );
};

export const TreeViewPage: React.FC = () => {
  const { treeId, nodeId } = useParams<{ treeId: string; nodeId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const treeIdValue = treeId || 'tekken-8';

  // Legacy: /tree/:slug#nodeId → canonical /tree/:slug/:shortNodeKey (preserves query string)
  useEffect(() => {
    if (nodeId) return;
    const raw = parseLegacyTreeViewHashNodeSegment(location.hash);
    if (!raw) return;
    navigate(`${buildTreeViewUrl(treeIdValue, raw)}${location.search}`, { replace: true });
  }, [treeIdValue, nodeId, location.hash, location.search, navigate]);

  const urlNodeSegment = nodeId ?? null;

  return (
    <AppProviders treeId={treeIdValue}>
      <AuthGate isDevelopment={false}>
        <TreeInteractorProvider key={treeIdValue} isDevMode={false} treeId={treeIdValue}>
          <TreeViewPageContent treeId={treeIdValue} urlNodeSegment={urlNodeSegment} />
        </TreeInteractorProvider>
      </AuthGate>
    </AppProviders>
  );
};
